import { Component, Input, OnInit, OnDestroy, Output, EventEmitter, ChangeDetectorRef, SimpleChanges, OnChanges, ViewChild, AfterViewInit, ElementRef, Injector } from '@angular/core';
import { FormGroup, ValidationErrors, Validators, FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil, distinctUntilChanged, debounceTime, filter } from 'rxjs/operators';
import { Product } from '../../shared/product-select/product-select.model';
import { ProductSelectComponent } from '../../shared/product-select/product-select.component';

/**
 * Represents a single line item within a document (e.g., invoice, quote).
 * Handles calculations, product selection, validation, and interaction for the line.
 */
@Component({
	selector: '[app-document-line]',
	templateUrl: './document-line.component.html',
	styleUrls: ['./document-line.component.scss']
})
export class DocumentLineComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
	/** The FormGroup representing this line item's data. */
	@Input() lineGroup!: FormGroup;
	/** The index of this line within the document's lines array. */
	@Input() lineIndex!: number;
	/** The current VAT percentage applicable to the document. */
	@Input() vatPercentage: number = 0;
	/** Flag indicating if the parent form has been submitted (for validation display). */
	@Input() submitted: boolean = false;
	/** Flag to enable debug logging. */
	@Input() debug: boolean = false;
	/** The list of available products for selection. */
	@Input() productsList: Product[] = [];
	/** Controls whether this line is closed (read-only/compact mode). */
	@Input() isClosed: boolean = false;

	/** Company on the open document, passed through to the add-product dialog. */
	get documentCompanyId(): number | null {
		const raw = this.lineGroup?.root?.get('head.company_id')?.value;
		const n = Number(raw);
		return Number.isFinite(n) && n > 0 ? n : null;
	}

	// REMOVED: globalVatIncluded - Each product now has individual VAT toggle

	/** Event emitter for removing this line item. */
	@Output() removeLine = new EventEmitter<number>();
	/** Emits when VAT included/excluded is toggled, so parent can update all lines. */
	@Output() vatIncludedChange = new EventEmitter<boolean>();

	/** Subject used to manage subscription cleanup on component destruction. */
	protected destroy$ = new Subject<void>();
	/** Flag to prevent calculation loops when updating price from total. */
	private isCalculatingReverse = false;
	
	/** Flag to prevent infinite calculation cycles during normal calculations. */
	private isCalculating = false;
	
	/** Flag to track when total amount has been manually set to preserve exact values. */
	private isTotalManuallySet = false;
	
	/** Flag to prevent flag reset during confirm process. */
	private isInConfirmProcess = false;
	/** Timeout for debouncing total amount changes. */
	private totalChangeTimeout: any;
	/** The current language setting for localization. */
	curentLang: string = localStorage.getItem('curentLang') || 'en';

	/** Reference to the child ProductSelectComponent. */
	@ViewChild(ProductSelectComponent) productSelectComponent: ProductSelectComponent | undefined;

	/**
	 * Creates an instance of DocumentLineComponent.
	 * @param cdr - Angular's ChangeDetectorRef for manual change detection.
	 * @param translate - Service for handling translations.
	 */
	constructor(
		private cdr: ChangeDetectorRef,
		private translate: TranslateService,
		private elementRef: ElementRef
	) {}

	/**
	 * Initializes the component, sets up initial values, and subscribes to value changes.
	 */
	ngOnInit(): void {
		if (!this.lineGroup) {
			console.error(`[ERROR/DocLine ${this.lineIndex}] ngOnInit ERROR: lineGroup is missing! Cannot proceed.`);
			return;
		}

		if (!this.lineGroup.get('discountType')) {
			this.lineGroup.addControl('discountType', new FormControl('amount'));
		} else if (!this.lineGroup.get('discountType')?.value) {
			this.lineGroup.get('discountType')?.setValue('amount');
		}

		// REMOVED: Global VAT sync - Each product now has individual VAT toggle

		this.subscribeToValueChanges();
		this.updateVatRateDisplay();
		this.calculateTotals(false);
		
		// Add manual triggers for key form controls that should trigger calculations
		this.addManualCalculationTriggers();
	}

	/**
	 * Lifecycle hook called after Angular initializes the component's views and child views.
	 */
	ngAfterViewInit(): void {
	}

	/**
	 * Detects changes to input properties and triggers necessary updates or recalculations.
	 * @param changes - Object containing the changed properties.
	 */
	ngOnChanges(changes: SimpleChanges): void {
		let needsRecalculate = false;
		let needsResubscribe = false;

		if (changes['productsList']) {
			const newList = changes['productsList'].currentValue as Product[];
			const newListLength = newList?.length ?? 0;
			if (newListLength > 0 && this.productSelectComponent) {
				this.productSelectComponent.triggerChangeDetection();
			}
		}

		if (changes['lineGroup']) {
			 if (!changes['lineGroup'].currentValue) {
				 console.error(`[ERROR/DocLine ${this.lineIndex}] ngOnChanges ERROR: Received null/undefined lineGroup!`);
				 this.destroy$.next();
				 return;
			 }
			 this.lineGroup = changes['lineGroup'].currentValue;
			 needsResubscribe = true;
			 needsRecalculate = true;
		}

		if (changes['vatPercentage'] && !changes['vatPercentage'].firstChange) {
			needsRecalculate = true;
		}

		// REMOVED: Global VAT change detection - Each product now has individual VAT toggle

		if (needsResubscribe) {
			this.destroy$.next();
			this.subscribeToValueChanges();
		}
		if (needsRecalculate) {
			 this.updateVatRateDisplay();
			 this.calculateTotals(false);
		}
	}

	/**
	 * Cleans up subscriptions when the component is destroyed.
	 */
	ngOnDestroy(): void {
		// Clear any pending timeout
		if (this.totalChangeTimeout) {
			clearTimeout(this.totalChangeTimeout);
		}
		this.destroy$.next();
		this.destroy$.complete();
	}

	/**
	 * Subscribes to value changes in relevant form controls to trigger calculations.
	 * DISABLED: This was causing infinite calculation cycles and floating-point precision errors.
	 */
	private subscribeToValueChanges(): void {
		// DISABLED: Automatic calculations were causing infinite cycles and floating-point errors
		// Calculations are now only triggered manually when needed
		// Only keep discount validation
		this.adjustDiscountValidation();
	}

	/**
	 * Updates the displayed VAT rate in the form control (which is typically disabled).
	 */
	private updateVatRateDisplay(): void {
		if (this.lineGroup.get('vat_rate')?.value !== this.vatPercentage) {
			this.lineGroup.get('vat_rate')?.setValue(this.vatPercentage, { emitEvent: false });
		}
	}

	/**
	 * Adjusts the validation rules for the discount control based on the selected discount type (amount or percentage).
	 */
	private adjustDiscountValidation(): void {
		const discountControl = this.lineGroup.get('discount');
		const discountType = this.lineGroup.get('discountType')?.value;
		if (!discountControl) return;

		discountControl.removeValidators(Validators.max(100));
		if (discountType === 'percentage') {
			discountControl.addValidators(Validators.max(100));
			// Only reset if the value is actually > 100 and it's a percentage
			if (discountControl.value > 100) {
				discountControl.patchValue(100, { emitEvent: false });
			}
		}
		discountControl.updateValueAndValidity({ emitEvent: false });
	}

	/**
	 * Calculates the line totals (subtotal, VAT amount, total amount) based on quantity, price, discount, and VAT settings.
	 * @param emitEvent - Whether to emit valueChanges events after patching values. Defaults to true.
	 */
	calculateTotals(emitEvent = true): void {
		if (!this.lineGroup) {
			return;
		}
		
		// Prevent infinite calculation cycles
		if (this.isCalculating || this.isCalculatingReverse) {
			return;
		}
		
		this.isCalculating = true;
		this.isCalculatingReverse = false;

		const qty = this.lineGroup.get('quantity')?.value ?? 0;
		let price = this.lineGroup.get('price_per_unit')?.value ?? 0;
		// Round the input price to avoid floating-point precision errors
		price = Math.round(price * 100) / 100;
		const discountValue = this.lineGroup.get('discount')?.value ?? 0;
		const discountType = this.lineGroup.get('discountType')?.value ?? 'amount';
		const isVatIncluded = this.lineGroup.get('isVatIncluded')?.value ?? false;
		const currentVatRate = this.vatPercentage;

		// Check if this is a tax-exempt item
		const isTaxExempt = currentVatRate === 0;

		// VAT included vs excluded should work differently
		let priceBeforeVat = price;
		if (isVatIncluded && currentVatRate > 0) {
			// When VAT is included, the price is the final price including VAT
			// We use it directly without extracting net price
			priceBeforeVat = price;
		} else {
			// When VAT is excluded, the price is the net price
		}

		let lineSubTotalBeforeDiscount = qty * priceBeforeVat;
		
		let lineDiscountAmount = 0;

		if (discountType === 'percentage') {
			const validDiscountPercent = Math.min(discountValue, 100);
			lineDiscountAmount = lineSubTotalBeforeDiscount * (validDiscountPercent / 100);
		} else {
			lineDiscountAmount = Math.min(discountValue, lineSubTotalBeforeDiscount);
			if (discountValue > lineSubTotalBeforeDiscount && lineSubTotalBeforeDiscount > 0) {
				this.lineGroup.get('discount')?.patchValue(lineSubTotalBeforeDiscount, { emitEvent: false });
				lineDiscountAmount = lineSubTotalBeforeDiscount;
			} else if (lineSubTotalBeforeDiscount <= 0) {
				lineDiscountAmount = 0;
				if (discountValue !== 0) this.lineGroup.get('discount')?.patchValue(0, { emitEvent: false });
			}
		}
		lineDiscountAmount = Math.max(0, lineDiscountAmount);

		let subTotal = lineSubTotalBeforeDiscount - lineDiscountAmount;
		
		let vatAmount, totalAmount;
		if (isTaxExempt) {
			// For tax-exempt items, no VAT calculation
			vatAmount = 0;
			totalAmount = subTotal;
		} else if (isVatIncluded) {
			// Discount applies to gross (tax-inclusive) line; then split net and VAT
			const grossAfterDiscount = subTotal;
			const netAfterDiscount =
				currentVatRate > 0 ? grossAfterDiscount / (1 + currentVatRate / 100) : grossAfterDiscount;
			vatAmount =
				currentVatRate > 0 ? grossAfterDiscount - netAfterDiscount : 0;
			totalAmount = grossAfterDiscount;
			subTotal = netAfterDiscount;
		} else {
			// When VAT is excluded, apply VAT calculation
			vatAmount = subTotal * (currentVatRate / 100);
			totalAmount = subTotal + vatAmount;
		}

		// Round to 2 decimal places to avoid floating-point precision errors
		const roundedSubTotal = Math.round(subTotal * 100) / 100;
		const roundedVatAmount = Math.round(vatAmount * 100) / 100;
		const roundedTotalAmount = Math.round(totalAmount * 100) / 100;

		if (!this.lineGroup) {
			return;
		}
		
		// Check if total was manually set - if so, preserve the exact values
		let finalSubTotal = roundedSubTotal;
		let finalVatAmount = roundedVatAmount;
		let finalTotalAmount = roundedTotalAmount;
		
		if (this.isTotalManuallySet) {
			// Preserve the manually set total and adjust VAT to match
			const currentTotal = this.lineGroup.get('total_amount')?.value;
			// ALWAYS preserve the manually set total if the flag is true
			if (currentTotal) {
				finalTotalAmount = currentTotal;
				finalVatAmount = currentTotal - roundedSubTotal;
				// Ensure VAT amount is not negative
				finalVatAmount = Math.max(0, finalVatAmount);
				} else {
				}
		} else {
			}
		
		// Update isPresenting based on discount type
		const currentDiscountType = this.lineGroup.get('discountType')?.value ?? 'amount';
		const isPresenting = currentDiscountType === 'percentage';
		
		this.lineGroup.patchValue({
			sub_total: finalSubTotal,
			vat_amount: finalVatAmount,
			total_amount: finalTotalAmount,
			isPresenting: isPresenting
		}, { emitEvent: emitEvent });

		// IMMEDIATE FORCE: Try to trigger summary calculation right after patchValue
		if (emitEvent) {
			// Try multiple immediate approaches
			this.forceSummaryCalculation();
		} else {
			// FORCE for ALL items when emitEvent=false to ensure summary is always updated
			this.forceSummaryCalculation();
		}

		if (!this.lineGroup) {
			return;
		}
		this.cdr.detectChanges();

		// Trigger summary calculation after line totals are updated
		// Re-enabled: Summary calculations are needed when price, discount, or quantity change
		if (emitEvent) {
			// Use Promise.resolve().then() to ensure form controls are updated before summary calculation
			Promise.resolve().then(() => {
				this.triggerSummaryCalculation();
			});
		}
		
		// Unlock calculation
		this.isCalculating = false;
	}

	/**
	 * Calculates the price per unit based on the total amount, quantity, discount, and VAT settings (reverse calculation).
	 * @param totalAmount - The target total amount for the line.
	 */
	calculatePriceFromTotal(totalAmount: number | null): void {
		if (!this.lineGroup) {
			return;
		}
		if (totalAmount === null || totalAmount < 0) {
			return;
		}
		
		// Prevent infinite calculation cycles
		if (this.isCalculating || this.isCalculatingReverse) {
			return;
		}

		this.isCalculatingReverse = true;
		this.isCalculating = true;

		const qty = this.lineGroup.get('quantity')?.value ?? 0;
		const discountValue = this.lineGroup.get('discount')?.value ?? 0;
		const discountType = this.lineGroup.get('discountType')?.value ?? 'amount';
		const isVatIncluded = this.lineGroup.get('isVatIncluded')?.value ?? false;
		const currentVatRate = this.vatPercentage;

		if (qty <= 0) {
			this.isCalculatingReverse = false;
			this.isCalculating = false;
			return;
		}

		// Check if this is a tax-exempt item
		const isTaxExempt = currentVatRate === 0;
		
		
		let subTotal, vatAmount;

		if (isTaxExempt) {
			// For tax-exempt items, total = subtotal, no VAT
			subTotal = totalAmount;
			vatAmount = 0;
		} else if (isVatIncluded) {
			const grossAfterDiscount = totalAmount;
			subTotal =
				currentVatRate > 0
					? grossAfterDiscount / (1 + currentVatRate / 100)
					: grossAfterDiscount;
			vatAmount =
				currentVatRate > 0 ? grossAfterDiscount - subTotal : 0;
		} else {
			// When VAT is excluded, total = subtotal + VAT
			// So: total = subtotal + (subtotal * vatRate/100)
			// Therefore: total = subtotal * (1 + vatRate/100)
			// So: subtotal = total / (1 + vatRate/100)
			subTotal = totalAmount / (1 + currentVatRate / 100);
			vatAmount = subTotal * (currentVatRate / 100);
		}

		let unitPriceBasis = 0;
		if (discountType === 'percentage') {
			const validDiscountPercent = Math.min(discountValue, 100);
			if (validDiscountPercent < 100) {
				if (!isVatIncluded && !isTaxExempt) {
					unitPriceBasis = subTotal / (1 - validDiscountPercent / 100);
				} else {
					unitPriceBasis = totalAmount / (1 - validDiscountPercent / 100);
				}
			} else {
				unitPriceBasis = totalAmount > 0 ? 0 : 0;
			}
		} else {
			const validDiscountAmount = Math.max(0, discountValue);
			if (!isVatIncluded && !isTaxExempt) {
				unitPriceBasis = subTotal + validDiscountAmount;
				if (validDiscountAmount > unitPriceBasis && unitPriceBasis > 0) {
					this.lineGroup.get('discount')?.patchValue(unitPriceBasis, { emitEvent: false });
					unitPriceBasis = subTotal + unitPriceBasis;
				}
			} else {
				unitPriceBasis = totalAmount + validDiscountAmount;
				if (validDiscountAmount > unitPriceBasis && unitPriceBasis > 0) {
					this.lineGroup.get('discount')?.patchValue(unitPriceBasis, { emitEvent: false });
					unitPriceBasis = totalAmount + unitPriceBasis;
				}
			}
		}

		let price = unitPriceBasis / qty;

		// Round to 2 decimal places to avoid floating-point precision errors
		const roundedPrice = Math.round(price * 100) / 100;
		const roundedSubTotal = Math.round(subTotal * 100) / 100;
		const roundedVatAmount = Math.round(vatAmount * 100) / 100;

		if (!this.lineGroup) {
			 this.isCalculatingReverse = false;
			 return;
		}
		// Preserve the exact total amount the user entered
		// Adjust VAT amount to ensure total matches exactly
		let finalVatAmount = roundedVatAmount;
		let finalTotalAmount = totalAmount; // Use the exact total amount the user entered
		
			// If the rounded values don't add up to the exact total, adjust VAT to compensate
			const calculatedTotal = roundedSubTotal + roundedVatAmount;
			if (Math.abs(calculatedTotal - totalAmount) >= 0.01) {
			finalVatAmount = totalAmount - roundedSubTotal;
			// Ensure VAT amount is not negative
			finalVatAmount = Math.max(0, finalVatAmount);
		}
		
		// Mark that the total has been manually set
		this.isTotalManuallySet = true;
		this.lineGroup.patchValue({
			price_per_unit: roundedPrice,
			sub_total: roundedSubTotal,
			vat_amount: finalVatAmount,
			total_amount: finalTotalAmount
		}, { emitEvent: false });

		// Notify parent form and trigger summary recalculation
		this.lineGroup.updateValueAndValidity({ onlySelf: false, emitEvent: true });

		if (!this.lineGroup) {
			 setTimeout(() => { 
				 this.isCalculatingReverse = false; 
				 this.isCalculating = false;
			 }, 0);
			 return;
		}

		setTimeout(() => {
			this.isCalculatingReverse = false;
			this.isCalculating = false;
			// Summary calculation is now triggered manually by onTotalAmountChange
			// this.triggerSummaryCalculation();
		}, 0);

		this.cdr.detectChanges();
	}

	/**
	 * Handles the selection of a product from the product dropdown.
	 * Updates the line item's details based on the selected product.
	 * @param product - The selected product object, or null if deselected.
	 */
	onProductSelection(product: Product | null): void {
		if (!this.lineGroup) {
			return;
		}

		if (!this.lineGroup.contains('product_id')) {
			console.error(`[ERROR/DocLine ${this.lineIndex}] Cannot patch product: 'product_id' control missing from lineGroup!`);
			return;
		}

		if (product) {
			const productName = this.getProductNameFromProduct(product);
			const productDescription = this.getProductDescriptionFromProduct(product);
			this.lineGroup.patchValue({
				product_id: product.product_id,
				item_name: productName,
				description: productDescription || productName,
				price_per_unit: product.price || 0,
				measurement_unit_id: product.measurement_unit_id,
				catalog_id: product.catalog_id,
			}, { emitEvent: false });

			if (!this.lineGroup) {
				 return;
			}
			this.calculateTotals(false);
			
			// Trigger summary calculation after product selection
			setTimeout(() => {
				this.triggerSummaryCalculation();
			}, 100);
		} else {
			this.lineGroup.patchValue({
				product_id: null,
				item_name: '',
				description: '',
				price_per_unit: 0,
				measurement_unit_id: null,
				catalog_id: null,
			}, { emitEvent: false });
			this.calculateTotals(false);
			
			// Trigger summary calculation after product deselection
			setTimeout(() => {
				this.triggerSummaryCalculation();
			}, 100);
		}

		if (!this.lineGroup) {
			 return;
		}
		this.lineGroup.get('product_id')?.markAsTouched();
		this.lineGroup.get('price_per_unit')?.markAsTouched();
		this.lineGroup.get('description')?.markAsTouched();

		if (!this.lineGroup) {
			 return;
		}
		this.lineGroup.updateValueAndValidity({ onlySelf: false, emitEvent: true });
		this.cdr.detectChanges();
	}

	/**
	 * Retrieves the localized product name based on the current language setting.
	 * Provides fallbacks if the primary language name is missing.
	 * @param item - The product object.
	 * @returns The localized product name, or an empty string if item is null.
	 */
	private getProductNameFromProduct(item: Product | null): string {
		if (!item) return '';
		switch (this.curentLang) {
			case 'ar': return item.product_name_ar || item.product_name_he || item.product_name_en || '';
			case 'he': return item.product_name_he || item.product_name_en || '';
			default: return item.product_name_en || item.product_name_he || '';
		}
	}

	/**
	 * Retrieves the localized product description based on the current language setting.
	 * Provides fallbacks if the primary language description is missing.
	 * @param item - The product object.
	 * @returns The localized product description, or an empty string if item is null.
	 */
	private getProductDescriptionFromProduct(item: Product | null): string {
		if (!item) return '';
		switch (this.curentLang) {
			case 'ar': return item.des_ar || item.des_he || item.des_en || '';
			case 'he': return item.des_he || item.des_en || '';
			default: return item.des_en || item.des_he || '';
		}
	}

	/**
	 * Toggles the discount type between 'percentage' and 'amount'.
	 */
	toggleDiscountType(): void {
		const currentType = this.lineGroup.get('discountType')?.value;
		const newType = currentType === 'percentage' ? 'amount' : 'percentage';
		this.lineGroup.get('discountType')?.setValue(newType);
		
		// Update isPresenting: true for percentage, false for amount
		const newIsPresenting = newType === 'percentage';
		this.lineGroup.get('isPresenting')?.setValue(newIsPresenting);
		
		// Trigger calculation after discount type change
		this.calculateTotals();
	}

	/**
	 * Toggles the 'isVatIncluded' flag for this individual product line.
	 * Each product now has its own independent VAT toggle.
	 */
	toggleVatIncluded(): void {
		const control = this.lineGroup.get('isVatIncluded');
		if (control) {
			const newValue = !control.value;
			// Always set the value individually for this product line
			control.setValue(newValue);
			
			// Manual calculation trigger after VAT toggle
			this.calculateTotals();
			
			// Trigger summary calculation after VAT toggle
			setTimeout(() => {
				this.triggerSummaryCalculation();
			}, 100);
		}
	}

	/**
	 * Emits an event to notify the parent component to remove this line item.
	 */
	onRemoveLine(): void {
		this.removeLine.emit(this.lineIndex);
	}

	/**
	 * Sets the confirm process flag to prevent flag resets during form validation.
	 * Called by parent document component when confirm button is clicked.
	 */
	setConfirmProcessFlag(): void {
		this.isInConfirmProcess = true;
		// Reset the flag after a delay to allow form validation to complete
		setTimeout(() => {
			this.isInConfirmProcess = false;
			}, 1000);
	}

	/**
	 * Handles manual total amount changes with reverse calculation and summary update.
	 * @param totalValue - The new total amount value
	 */
	onTotalAmountChange(totalValue: number): void {
		// Prevent infinite loops
		if (this.isCalculating || this.isCalculatingReverse) {
			return;
		}
		
		// Check if the value is valid and not empty
		if (totalValue === null || totalValue === undefined || isNaN(totalValue)) {
			return;
		}
		
		// Clear any existing timeout
		if (this.totalChangeTimeout) {
			clearTimeout(this.totalChangeTimeout);
		}
		
		// Debounce the calculation to avoid interference with typing
		this.totalChangeTimeout = setTimeout(() => {
			// Only perform reverse calculation if the value is different from current
			const currentTotal = this.lineGroup.get('total_amount')?.value;
			if (Math.abs(totalValue - currentTotal) < 0.01) {
				return;
			}
			
			// Perform reverse calculation
			this.calculatePriceFromTotal(totalValue);
			
			// Trigger summary calculation after the patchValue has settled
			Promise.resolve().then(() => {
				this.triggerSummaryCalculation();
			});
		}, 500); // 500ms debounce
	}

	/**
	 * Triggers summary calculation by emitting a custom event to the parent component.
	 * This is needed because automatic FormArray value changes are disabled.
	 */
	private triggerSummaryCalculation(): void {
		// Emit a custom event to trigger summary calculation
		// The parent DocumentLinesComponent should listen for this event
		const event = new CustomEvent('lineCalculationChanged', {
			detail: { lineIndex: this.lineIndex },
			bubbles: true
		});
		window.dispatchEvent(event);
	}
	
	/**
	 * Forces summary calculation using multiple immediate approaches.
	 * This is a more aggressive method to ensure summary calculation happens.
	 */
	private forceSummaryCalculation(): void {
		// Approach 1: Custom event (immediate)
		const event = new CustomEvent('lineCalculationChanged', {
			detail: { lineIndex: this.lineIndex },
			bubbles: true
		});
		window.dispatchEvent(event);
		
		// Approach 2: Direct DOM access (immediate)
		const parentElement = this.elementRef.nativeElement.closest('app-document-lines');
		if (parentElement) {
			const componentInstance = (parentElement as any).__ngContext__?.[0];
			if (componentInstance && componentInstance.calculateLinesSummary) {
				componentInstance.calculateLinesSummary();
			}
		}
		
		// Approach 3: Document querySelector (immediate)
		const documentLinesComponent = document.querySelector('app-document-lines');
		if (documentLinesComponent) {
			const docComponent = (documentLinesComponent as any).__ngContext__?.[0];
			if (docComponent && docComponent.calculateLinesSummary) {
				docComponent.calculateLinesSummary();
			}
		}
		
		// Approach 4: Try to find any component with calculateLinesSummary method
		const allElements = document.querySelectorAll('*');
		for (let i = 0; i < allElements.length; i++) {
			const element = allElements[i];
			const component = (element as any).__ngContext__?.[0];
			if (component && component.calculateLinesSummary) {
				component.calculateLinesSummary();
				break;
			}
		}
		
		// Approach 5: Use global reference
		if ((window as any).documentLinesComponentActive && (window as any).documentLinesComponent) {
			const globalComponent = (window as any).documentLinesComponent;
			if (globalComponent && globalComponent.calculateLinesSummary) {
				globalComponent.calculateLinesSummary();
			}
		}
	}

	/**
	 * Adds manual calculation triggers for key form controls.
	 * This ensures calculations happen when data is entered or changed.
	 * Only essential triggers are enabled to prevent infinite cycles.
	 */
	private addManualCalculationTriggers(): void {
		// Only add essential triggers - discount and quantity
		const discountControl = this.lineGroup.get('discount');
		if (discountControl) {
			discountControl.valueChanges.pipe(
				distinctUntilChanged(),
				filter(() => !this.isCalculating && !this.isCalculatingReverse),
				takeUntil(this.destroy$)
			).subscribe(() => {
				// Only reset manual total flag if not in confirm process
				const isInConfirmProcess = (window as any).isInConfirmProcess || this.isInConfirmProcess;
				if (this.isTotalManuallySet && !isInConfirmProcess) {
					this.isTotalManuallySet = false;
				}
				this.adjustDiscountValidation();
				this.calculateTotals();
			});
		}
		
		const quantityControl = this.lineGroup.get('quantity');
		if (quantityControl) {
			quantityControl.valueChanges.pipe(
				distinctUntilChanged(),
				filter(() => !this.isCalculating && !this.isCalculatingReverse),
				takeUntil(this.destroy$)
			).subscribe(() => {
				// Only reset manual total flag if not in confirm process
				const isInConfirmProcess = (window as any).isInConfirmProcess || this.isInConfirmProcess;
				if (this.isTotalManuallySet && !isInConfirmProcess) {
					this.isTotalManuallySet = false;
				}
				this.calculateTotals();
			});
		}
		
		const priceControl = this.lineGroup.get('price_per_unit');
		if (priceControl) {
			priceControl.valueChanges.pipe(
				distinctUntilChanged(),
				debounceTime(100), // Add debounce to prevent rapid changes
				filter(() => !this.isCalculating && !this.isCalculatingReverse),
				takeUntil(this.destroy$)
			).subscribe(() => {
				// Only reset manual total flag if not in confirm process
				const isInConfirmProcess = (window as any).isInConfirmProcess || this.isInConfirmProcess;
				if (this.isTotalManuallySet && !isInConfirmProcess) {
					this.isTotalManuallySet = false;
				}
				this.calculateTotals();
			});
		}
		
		// DISABLED: total_amount trigger was causing infinite calculation cycles
		// Manual total editing is now only triggered by HTML (onInput) events
		}

	/**
	 * Checks if a specific form control is invalid and should display an error.
	 * @param controlName - The name of the form control to check.
	 * @returns True if the control is invalid and has been touched, dirtied, or the form submitted.
	 */
	isInvalid(controlName: string): boolean {
		const control = this.lineGroup.get(controlName);
		return !!control && control.invalid && (control.dirty || control.touched || this.submitted);
	}

	/**
	 * Gets the translated error message for a specific form control.
	 * @param controlName - The name of the form control.
	 * @returns The translated error message, or an empty string if the control is valid or has no errors.
	 */
	getErrorMessage(controlName: string): string {
		const control = this.lineGroup.get(controlName);
		if (!control || !this.isInvalid(controlName) || !control.errors) {
			return '';
		}
		const errors: ValidationErrors = control.errors;
		let fieldKey = `routes.document.${controlName}`;
		switch (controlName) {
			case 'product_id': fieldKey = 'routes.document.product'; break;
			case 'price_per_unit': fieldKey = 'routes.document.pricePerUnit'; break;
			case 'total_amount': fieldKey = 'routes.document.total'; break;
			case 'quantity': fieldKey = 'routes.document.quantity'; break;
			case 'discount': fieldKey = 'routes.document.discount'; break;
			case 'description': fieldKey = 'routes.document.description'; break;
		}
		const fieldName = this.translate.instant(fieldKey);

		if (errors['required']) {
			return this.translate.instant('global.Validation.Required', { field: fieldName });
		}
		if (errors['min']) {
			return this.translate.instant('global.Validation.Min', { field: fieldName, min: errors['min'].min });
		}
		if (errors['max']) {
			return this.translate.instant('global.Validation.Max', { field: fieldName, max: errors['max'].max });
		}
		const firstErrorKey = Object.keys(errors)[0];
		return this.translate.instant('global.Validation.Invalid', { field: fieldName, error: firstErrorKey });
	}

	/**
	 * Checks if the current screen size is mobile (width <= 768px).
	 * @returns True if the screen is mobile, false otherwise.
	 */
	get isMobile(): boolean {
        return window.innerWidth <= 768;
    }
}